import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import ReactQuill from "react-quill";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import "react-quill/dist/quill.snow.css";
import { Chip, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  collection,
  addDoc,
  serverTimestamp,
  updateDoc,
  getDocs,
  where,
  query,
  doc,
  getDoc,
  limit,
  orderBy,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import EditIcon from "@mui/icons-material/Edit";
import { db } from "../../../../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress } from "@mui/material";
import * as yup from "yup";
import { AsyncImage } from "loadable-image";
import CloseIcon from "@mui/icons-material/Close";
import { AppContext } from "../../../../store/AppContext";
import { colornames } from "color-name-list";
import { SketchPicker } from "react-color";
import { Popover } from "@mui/material";
import imageCompression from "browser-image-compression";

// Prepare color mapping
const colorMap = {};
colornames.forEach((color) => {
  colorMap[color.name.toLowerCase()] = color.hex.toLowerCase();
});

// Function to get hex code from color name or value
const getColorHex = (colorInput) => {
  if (!colorInput) return "#ffffff"; // Default to white if empty

  // Check if input is already a hex code
  if (/^#[0-9A-F]{6}$/i.test(colorInput)) {
    return colorInput.toLowerCase();
  }

  // Try to find by color name
  const hexFromName = colorMap[colorInput.toLowerCase()];
  if (hexFromName) return hexFromName;

  // Default to white if no match found
  return "#ffffff";
};

const productSchema = yup
  .object()
  .shape({
    productName: yup.string().required("Product name is required"),
    productCategory: yup.string(),
    categories: yup
      .array()
      .of(yup.string().required("Category ID is required"))
      .min(1, "At least one category is required"),
    productPrice: yup
      .number()
      .typeError("Price must be a number")
      .positive("Price must be positive")
      .required("Price is required"),
    productDiscountedPrice: yup
      .number()
      .nullable()
      .positive("Price must be positive")
      .transform((_, val) => (val === "" ? undefined : Number(val))), // Convert empty string to null
    productUnit: yup.string().required("Product unit is required"),
    unitQuantity: yup
      .number()
      .typeError("Unit quantity must be a number")
      .positive("Unit quantity must be positive")
      .required("Unit quantity is required"),
    quantity: yup
      .number()
      .typeError("Quantity must be a number")
      .integer()
      .min(0, "Quantity can't be negative")
      .required("Quantity is required"),
    skuId: yup.string().required("SKU ID is required"),
    shippingCost: yup
      .number()
      .typeError("Shipping cost must be a number")
      .min(0, "Shipping cost can't be negative")
      .required("Shipping cost is required"),
    taxRate: yup
      .number()
      .typeError("Tax rate must be a number")
      .min(0, "Tax rate can't be negative")
      .max(100, "Tax rate can't exceed 100%")
      .required("Tax rate is required"),
    variants: yup.array().of(
      yup.object().shape({
        optionName: yup.string().required("Option name is required"),
        optionValue: yup
          .array()
          .of(
            yup.lazy((value, context) => {
              // Check if this is a color variant
              if (typeof value === "object" && value !== null) {
                return yup.object().shape({
                  name: yup.string().required("Color name is required"),
                  hex: yup.string().required("Color value is required"),
                });
              }
              // Default to string validation for other options
              return yup.string().required("Option value is required");
            })
          )
          .min(1, "At least one option value is required")
          .required("Option values are required"),
      })
    ),
    variantDetails: yup.array().of(
      yup.object().shape({
        sku: yup.string().required("SKU is required"),
        inventory: yup
          .number()
          .typeError("Inventory must be a number")
          .integer()
          .min(0, "Inventory can't be negative")
          .required("Inventory is required"),
        price: yup
          .number()
          .typeError("Price must be a number")
          .positive("Price must be positive")
          .required("Price is required"),
        discountedPrice: yup
          .number()
          .nullable()
          .positive("Price must be positive")
          .transform((_, val) => (val === "" ? undefined : Number(val))),
        combination: yup
          .array()
          .of(
            yup.object().shape({
              name: yup.string().required(),
              value: yup.lazy((val) => {
                if (typeof val === "object" && val !== null) {
                  return yup.object().shape({
                    name: yup.string().required(),
                    hex: yup.string().required(),
                  });
                }
                return yup.string().required();
              }),
              originalValue: yup.lazy((val) => {
                if (typeof val === "object" && val !== null) {
                  return yup.object().shape({
                    name: yup.string().required(),
                    hex: yup.string().required(),
                  });
                }
                return yup.string();
              }),
            })
          )
          .required(),
      })
    ),
  })
  .test(
    "variant-consistency",
    "Variant details must match variant combinations",
    function (value) {
      if (!value.variants || !value.variantDetails) return true;

      // Check all variant details have matching combinations
      const variantNames = value.variants.map((v) =>
        v.optionName.toLowerCase()
      );

      for (const detail of value.variantDetails) {
        if (!detail.combination) return false;

        // Check each combination matches a variant
        for (const combo of detail.combination) {
          if (!variantNames.includes(combo.name.toLowerCase())) {
            return false;
          }
        }

        // Check we have all variants represented
        if (detail.combination.length !== value.variants.length) {
          return false;
        }
      }

      return true;
    }
  );

const AddNewProduct = () => {
  const { productsTabInfo, setProductsTabInfo, setHeaderData } =
    useContext(AppContext);
  const { setLoader } = useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const isInitialRender = useRef(true);
  const [images, setImages] = useState(
    productsTabInfo?.selectedProduct?.images || []
  );
  const [value1, setValue1] = useState(
    productsTabInfo?.selectedProduct?.description || ""
  );
  const [activeTab, setActiveTab] = useState("productInfo");
  const [isEditing, setIsEditing] = useState([{ 0: false }]);
  const [loading, setLoading] = useState(false);
  const [isUnitPopupOpen, setIsUnitPopupOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [selectedCouponIds, setSelectedCouponIds] = useState([]);
  const [initialCouponIds, setInitialCouponIds] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);

  const couponDiscountLabel = (c) =>
    c.discountType === "percentage"
      ? `${c.discountValue}% off`
      : `₹${c.discountValue} off`;

  const isGlobalCoupon = (c) =>
    !(c.applicableProducts || []).length &&
    !(c.applicableCategories || []).length;

  const fetchCategories = async () => {
    setLoader(true);
    try {
      const categoriesCollectionRef = collection(db, "categories");
      const querySnapshot = await getDocs(categoriesCollectionRef);
      const categoriesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoader(false);
    }
  };

  const fetchCouponsForProduct = async () => {
    setCouponsLoading(true);
    try {
      const snap = await getDocs(collection(db, "coupons"));
      const selected = productsTabInfo?.selectedProduct;
      // Product docs may use either Firestore doc id or the stored `id` field.
      const productKeys = [
        selected?.id,
        selected?.docId,
      ].filter(Boolean);

      const all = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          // Active first, then by code
          const aActive = a.active === false ? 1 : 0;
          const bActive = b.active === false ? 1 : 0;
          if (aActive !== bActive) return aActive - bActive;
          return String(a.code || "").localeCompare(String(b.code || ""));
        });

      const linkedIds = productKeys.length
        ? all
            .filter((c) =>
              (c.applicableProducts || []).some((pid) =>
                productKeys.includes(pid)
              )
            )
            .map((c) => c.id)
        : [];

      // Show every coupon (including inactive) so the list is never empty
      // when coupons exist but are toggled off in Coupons tab.
      setAvailableCoupons(all);
      setSelectedCouponIds(linkedIds);
      setInitialCouponIds(linkedIds);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setAvailableCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  };

  const syncProductCoupons = async (productId) => {
    const toAdd = selectedCouponIds.filter(
      (id) => !initialCouponIds.includes(id)
    );
    const toRemove = initialCouponIds.filter(
      (id) => !selectedCouponIds.includes(id)
    );

    await Promise.all([
      ...toAdd.map((couponId) =>
        updateDoc(doc(db, "coupons", couponId), {
          applicableProducts: arrayUnion(productId),
          updatedDate: new Date(),
        })
      ),
      ...toRemove.map((couponId) =>
        updateDoc(doc(db, "coupons", couponId), {
          applicableProducts: arrayRemove(productId),
          updatedDate: new Date(),
        })
      ),
    ]);
  };

  useEffect(() => {
    fetchCategories();
    fetchCouponsForProduct();
  }, [productsTabInfo?.selectedProduct?.id]);

  const handleColorChange = (index, optionIndex, color) => {
    const updatedValues = [...(watch().variants?.[index]?.optionValue || [])];
    updatedValues[optionIndex] =
      typeof color === "string" ? color : color?.hex || color;
    setValue(`variants.${index}.optionValue`, updatedValues);
  };

  const productUnits = [
    "piece",
    "kg",
    "gm",
    "ml",
    "litre",
    "mm",
    "ft",
    "meter",
    "sq. ft.",
    "sq. meter",
    "km",
    "set",
    "hour",
    "day",
    "bunch",
    "bundle",
    "month",
    "year",
    "service",
    "work",
    "packet",
    "box",
    "pound",
    "dozen",
    "gunta",
    "pair",
    "minute",
    "quintal",
    "ton",
    "capsule",
    "tablet",
    "plate",
    "inch",
    "ounce",
    "bottle",
    "night",
    "tour",
  ];

  const productInfoRef = useRef(null);
  const productMediaRef = useRef(null);
  const inventoryRef = useRef(null);
  const shippingRef = useRef(null);
  const couponsRef = useRef(null);
  const variantsRef = useRef(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: productsTabInfo?.selectedProduct
      ? productsTabInfo?.selectedProduct
      : {
          productName: "",
          productCategory: "",
          productPrice: "",
          productDiscountedPrice: "",
          productUnit: "piece",
          quantity: "",
          skuId: "",
          shippingCost: "",
          taxRate: "",
          variants: [],
          categories: [],
          variantDetails: [],
        },
    resolver: yupResolver(productSchema),
  });

  const productName = watch("productName");
  const productCategory = watch("productCategory");
  const skuId = watch("skuId");
  const productDiscountedPrice = watch("productDiscountedPrice");

  const handleEditClick = (index) => {
    setIsEditing((prev) => ({
      ...prev,
      [index]: true,
    }));
  };

  const handleDoneClick = (index) => {
    const updatedVariants = [...watch().variants];

    if (!updatedVariants[index].optionName?.trim()) {
      setError(`variants.${index}.optionName`, {
        type: "manual",
        message: "Option Name is required",
      });
      return;
    }

    // Check if at least one valid option value exists
    const optionValues = updatedVariants[index].optionValue || [];
    const isValid = optionValues.some((value) => {
      if (updatedVariants[index].optionName.toLowerCase() === "color") {
        return value?.name?.trim() && value?.hex?.trim();
      }
      return value?.trim();
    });

    if (!isValid) {
      setError(`variants.${index}.optionValue`, {
        type: "manual",
        message: "At least one valid option value is required",
      });
      return;
    }

    clearErrors(`variants.${index}.optionName`);
    clearErrors(`variants.${index}.optionValue`);

    setIsEditing((prev) => ({
      ...prev,
      [index]: false,
    }));
  };

  const handleDeleteOption = (index, optionIndex) => {
    const updatedOptions = [...(watch().variants?.[index]?.optionValue || [])];

    if (updatedOptions.length > 1) {
      updatedOptions.splice(optionIndex, 1);
      setValue(`variants.${index}.optionValue`, updatedOptions);
    }
  };

  console.log(errors, "----->errors");

  const quickMenu = [
    { title: "Product Information", ref: "productInfo" },
    { title: "Product Media", ref: "productMedia" },
    { title: "Inventory", ref: "inventory" },
    { title: "Shipping & Tax", ref: "shipping" },
    { title: "Coupons", ref: "coupons" },
    { title: "Variants", ref: "variants" },
  ];

  const { fields, append: createVariant } = useFieldArray({
    control,
    name: "variants",
  });

  const handleScrollToSection = (section) => {
    const sectionRef = {
      productInfo: "productInfoRef",
      productMedia: "productMediaRef",
      inventory: "inventoryRef",
      shipping: "shippingRef",
      coupons: "couponsRef",
      variants: "variantsRef",
    };

    setActiveTab(section);

    const sectionElement = document.getElementById(sectionRef[section]);
    if (sectionElement) {
      sectionElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleDeleteVariant = (index) => {
    setValue(
      "variants",
      watch().variants.filter((_, i) => i !== index)
    );
  };

  const generateCombinations = (variants) => {
    if (variants.length === 0) return [];
    return variants.reduce((acc, variant) => {
      const newCombinations = [];
      variant.optionValue.forEach((value) => {
        const displayValue =
          variant.optionName.toLowerCase() === "color"
            ? value.name // Only show the color name, not the hex
            : value;

        if (acc.length === 0) {
          newCombinations.push([
            {
              name: variant.optionName,
              value: displayValue,
              originalValue: value,
            },
          ]);
        } else {
          acc.forEach((comb) => {
            newCombinations.push([
              ...comb,
              {
                name: variant.optionName,
                value: displayValue,
                originalValue: value,
              },
            ]);
          });
        }
      });
      return newCombinations;
    }, []);
  };
  const variants = watch("variants");
  const combinations = generateCombinations(variants);

  const handleAddNewOptionField = (index) => {
    const currentOptionValues = watch(`variants.${index}.optionValue`) || [];
    setValue(`variants.${index}.optionValue`, [...currentOptionValues, ""]);
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files?.length) return;

    setLoading(true);
    const storage = getStorage();

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Optimal compression settings for web performance
        const compressionOptions = {
          maxSizeMB: 0.1, // 100KB strict limit
          maxWidthOrHeight: 1200, // Optimal display size
          useWebWorker: true, // Non-blocking compression
          fileType: "image/webp", // Best modern format
          initialQuality: 0.7, // Balanced quality (70%)
          preserveExif: false, // Strip metadata
          alwaysKeepResolution: false, // Allow downscaling
          maxIteration: 5, // Retry attempts
          fileSizeTarget: 102400, // Explicit 100KB target
        };

        // First compression pass
        let compressedFile = await imageCompression(file, compressionOptions);

        // Progressive quality reduction if needed
        const qualitySteps = [0.6, 0.5, 0.4];
        let step = 0;

        while (compressedFile.size > 102400 && step < qualitySteps.length) {
          compressionOptions.initialQuality = qualitySteps[step];
          compressionOptions.maxWidthOrHeight = 1000 - step * 200; // 1000px → 800px → 600px
          compressedFile = await imageCompression(file, compressionOptions);
          step++;
        }

        // Final validation
        if (compressedFile.size > 102400) {
          throw new Error(
            `Image ${file.name} exceeds 100KB limit after compression`
          );
        }

        // Smart filename with hash
        const fileExt = file.name.split(".").pop();
        const fileNameHash =
          Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
        const optimizedFileName = `img_${fileNameHash}.webp`;

        // Upload with cache control
        const storageRef = ref(storage, `products/${optimizedFileName}`);
        await uploadBytes(storageRef, compressedFile, {
          cacheControl: "public,max-age=31536000,immutable", // 1 year cache
        });

        return await getDownloadURL(storageRef);
      });

      const uploadedImages = await Promise.all(uploadPromises);
      console.log(uploadedImages, "uploaded");
      setImages((prev) => [...prev, ...uploadedImages]);
    } catch (error) {
      console.error("Image optimization failed:", error);
      alert(
        `Upload error: ${error.message}\nPlease use images under 2MB with clear content.`
      );
    } finally {
      setLoading(false);
    }
  };
  const generateRandomSKU = () => {
    // Generate a random 6-digit number
    const randomNum = Math.floor(100000 + Math.random() * 900000);
    return `SKU-${randomNum}`;
  };

  useEffect(() => {
    // Only generate SKU if it's a new product (no existing SKU)
    if (
      (!productsTabInfo?.selectedProduct?.skuId && !watch("skuId")) ||
      !productsTabInfo?.selectedProduct?.id
    ) {
      const generatedSKU = generateRandomSKU();
      setValue("skuId", generatedSKU);
    }
  }, [productsTabInfo?.selectedProduct, setValue, watch]);

  useEffect(() => {
    console.log(productDiscountedPrice, "====>");
    const currentDetails = watch("variantDetails") || [];
    const mainSKU = skuId;
    const mainInventory = Number(watch("quantity")) || 0;
    const mainPrice = Number(watch("productPrice")) || 0;

    const newDetails = combinations?.map((comb, index) => {
      const currentDetail = currentDetails[index] || {};
      console.log(comb, "comb---->");
      const optionValues = comb
        ?.map((c) =>
          c.value?.name
            ? c?.value?.name?.replace(/\s+/g, "-")
            : c?.value?.replace(/\s+/g, "-")
        )
        .join("-");
      const generatedSKU = mainSKU
        ? `${mainSKU}-${optionValues}`
        : generateRandomSKU();
      console.log(currentDetail, "--->currect");
      return {
        ...currentDetail,
        sku: generatedSKU,
        inventory:
          currentDetail.inventory !== undefined
            ? currentDetail.inventory
            : mainInventory,
        price:
          currentDetail.price !== undefined ? currentDetail.price : mainPrice,
        discountedPrice:
          currentDetail.discountedPrice === undefined ||
          currentDetail.discountedPrice === null ||
          currentDetail.discountedPrice === 0
            ? currentDetail.price
            : currentDetail.discountedPrice,
        combination: comb,
      };
    });

    if (
      JSON.stringify(newDetails) !== JSON.stringify(watch("variantDetails"))
    ) {
      setValue("variantDetails", newDetails);
    }
  }, [combinations, skuId, watch("productPrice"), setValue, watch]);

  useEffect(() => {
    const sections = [
      { id: "productInfo", ref: productInfoRef },
      { id: "productMedia", ref: productMediaRef },
      { id: "inventory", ref: inventoryRef },
      { id: "shipping", ref: shippingRef },
      { id: "coupons", ref: couponsRef },
      { id: "variants", ref: variantsRef },
    ];

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.6,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = sections.find(
            (section) => section.ref.current === entry.target
          )?.id;
          if (sectionId) {
            setActiveTab(sectionId);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    sections.forEach((section) => {
      if (section.ref.current) {
        observer.observe(section.ref.current);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section.ref.current) {
          observer.unobserve(section.ref.current);
        }
      });

      // Keep page / totalPages / selectedCategory when leaving the form
      // so returning from edit restores the same list pagination.
      setProductsTabInfo((prev) => ({
        ...prev,
        addProduct: false,
        selectedProduct: null,
      }));
    };
  }, []);

  console.log(errors, "errors====>");

  const AddNewProducts = async (data) => {
    const processVariants = (variants) => {
      const variantOptions = {};

      variants.map((variant) => {
        const processedValues = variant.optionValue.map((value) => {
          if (
            variant.optionType === "color" ||
            variant.optionType === "Color"
          ) {
            return typeof value === "object"
              ? value
              : { name: value, hex: getColorHex(value) };
          }

          return value;
        });

        variantOptions[variant.optionName] = processedValues.map((v) =>
          typeof v === "object" ? v.name : v
        );
      });

      return { variantOptions };
    };

    const { variantOptions } = processVariants(data.variants);

    setLoader(true);

    try {
      const productsCollection = collection(db, "products");

      const q = query(
        productsCollection,

        where("id", "==", productsTabInfo?.selectedProduct?.id || "")
      );

      const querySnapshot = await getDocs(q);

      let productId;

      if (!querySnapshot.empty) {
        const existingDoc = querySnapshot.docs[0];

        const productRef = doc(db, "products", existingDoc.id);

        const previousData = existingDoc.data();

        await addDoc(collection(db, "audit_logs"), {
          productId: existingDoc.id,

          productName: previousData.productName,

          previousData,

          action: "Updated",

          timestamp: serverTimestamp(),
        });

        await updateDoc(productRef, {
          ...data,

          images,

          productDiscountedPrice:
            data?.productDiscountedPrice === null ||
            data?.productDiscountedPrice === undefined ||
            data?.productDiscountedPrice === 0
              ? data?.productPrice
              : data?.productDiscountedPrice,

          description: value1,

          ...variantOptions,

          productNameLower: data?.productName?.toLowerCase() || "",

          updatedDate: serverTimestamp(),
        });

        productId = existingDoc.id;

        console.log("Product updated with ID: ", productId);
      } else {
        const nowMs = Date.now();
        const docRef = await addDoc(productsCollection, {
          ...data,

          images,

          productDiscountedPrice:
            data?.productDiscountedPrice === null ||
            data?.productDiscountedPrice === undefined ||
            data?.productDiscountedPrice === 0
              ? data?.productPrice
              : data?.productDiscountedPrice,

          description: value1,

          ...variantOptions,

          active: true,
          // Append to end of rearrange order (position asc). Do NOT use
          // -Date.now() — that always beats rearrange indices 0,1,2… on the site.
          position: nowMs,
          createdAtMs: nowMs,

          productNameLower: data?.productName?.toLowerCase(),

          createdDate: serverTimestamp(),
          updatedDate: serverTimestamp(),

          categories: data.categories || [],
        });

        await updateDoc(doc(db, "products", docRef.id), {
          id: docRef.id,
          position: nowMs,
          createdAtMs: nowMs,
        });

        productId = docRef.id;

        console.log("New product added with ID: ", productId);

        await addDoc(collection(db, "audit_logs"), {
          productId: docRef.id,

          productName: data.productName,

          previousData: null,

          action: "Created",

          timestamp: serverTimestamp(),
        });
      }

      if (isNewCategory || categories.length === 0) {
        const categoryQuery = query(
          collection(db, "categories"),

          where("categoryName", "==", newCategoryName)
        );

        const categoryQuerySnapshot = await getDocs(categoryQuery);

        let categoryId;

        if (categoryQuerySnapshot.empty) {
          const categoryRef = await addDoc(collection(db, "categories"), {
            categoryName: newCategoryName,

            images: images.length > 0 ? [images[0]] : [],

            products: [productId],
            productCount: 1,

            createdDate: serverTimestamp(),
          });

          categoryId = categoryRef.id;

          console.log("New category created with ID: ", categoryId);
        } else {
          const existingCategoryDoc = categoryQuerySnapshot.docs[0];

          categoryId = existingCategoryDoc.id;

          const updatedProducts = [
            ...new Set([
              ...(existingCategoryDoc.data().products || []),
              productId,
            ]),
          ];

          await updateDoc(doc(db, "categories", categoryId), {
            products: updatedProducts,
            productCount: updatedProducts.length,
          });

          console.log(
            `Product ID ${productId} added to existing category ${categoryId}`
          );
        }

        await updateDoc(doc(db, "products", productId), {
          categories: [categoryId],
        });
      } else {
        if (data.categories && data.categories.length > 0) {
          for (const categoryId of data.categories) {
            const categoryRef = doc(db, "categories", categoryId);

            const categoryDoc = await getDoc(categoryRef);

            if (categoryDoc.exists()) {
              const categoryData = categoryDoc.data();

              const updatedProducts = [
                ...new Set([...(categoryData.products || []), productId]),
              ];

              await updateDoc(categoryRef, {
                products: updatedProducts,
                productCount: updatedProducts.length,
              });

              console.log(
                `Product ID ${productId} added to category ${categoryId}`
              );
            }
          }
        }
      }

      // Link / unlink coupons from this product (coupon.applicableProducts).
      try {
        await syncProductCoupons(productId);
      } catch (couponErr) {
        console.error("Product saved but coupon linking failed:", couponErr);
        alert(
          "Product saved, but updating coupons failed: " +
            (couponErr.message || "unknown error")
        );
      }

      setLoader(false);

      const wasNewProduct = !productsTabInfo?.selectedProduct?.id;

      // Preserve list page when editing; only jump to page 1 for brand-new products.
      setProductsTabInfo((prevState) => ({
        ...prevState,
        addProduct: false,
        selectedProduct: null,
        page: wasNewProduct ? 1 : prevState.page || 1,
        trigger: !prevState?.trigger,
      }));

      setHeaderData();
    } catch (e) {
      setLoader(false);

      console.error("Error processing product: ", e);
    }
  };

  useEffect(() => {
    // New products go to the end of Featured/rearrange order until moved.
    if (!productsTabInfo?.selectedProduct?.id) {
      setValue("position", Date.now());
    }
  }, [productsTabInfo?.selectedProduct, setValue]);

  console.log(skuId, "==skuid");

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    handleSubmit(AddNewProducts)();
  }, [productsTabInfo?.trigger]);

  const ColorPickerWithName = ({ value, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [colorData, setColorData] = useState(() =>
      typeof value === "object" ? value : { name: "", hex: value || "#ffffff" }
    );

    // Only sync from props if value actually changed (e.g. from parent reset)
    useEffect(() => {
      const newValue =
        typeof value === "object"
          ? value
          : { name: "", hex: value || "#ffffff" };

      if (newValue.hex !== colorData.hex || newValue.name !== colorData.name) {
        setColorData(newValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
      onChange?.(colorData); // Emit on Done
    };

    const handleColorChange = (color) => {
      setColorData((prev) => ({ ...prev, hex: color.hex }));
    };

    const handleNameChange = (e) => {
      const updated = { ...colorData, name: e.target.value };
      setColorData(updated);
    };

    const handleNameBlur = () => {
      onChange?.(colorData); // Emit on blur
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.target.blur(); // Trigger blur handler
      }
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
        }}
      >
        <div
          style={{
            width: isMobile ? "80px" : "40px",
            height: isMobile ? "80px" : "40px",
            backgroundColor: colorData.hex,
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={handleClick}
          aria-label="Color picker"
        />
        <TextField
          label="Color Name"
          value={colorData.name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size={isMobile ? "medium" : "small"}
          fullWidth
        />
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <SketchPicker color={colorData.hex} onChange={handleColorChange} />
          <div
            style={{
              padding: "8px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button variant="contained" size="small" onClick={handleClose}>
              Done
            </Button>
          </div>
        </Popover>
      </div>
    );
  };

  // Responsive styles
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          width: "95%",
          margin: "20px auto",
        },
        sidebar: {
          display: "none", // Hidden on mobile, replaced with drawer
        },
        content: {
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        },
        scrollContent: {
          height: "auto",
          overflowY: "visible",
        },
        section: {
          padding: "16px",
          borderRadius: "8px",
        },
        sectionHeight: {
          height: "auto",
          minHeight: "auto",
        },
        imageGrid: {
          gap: "8px",
        },
        imagePreview: {
          width: "80px",
          height: "80px",
        },
        formFields: {
          flexDirection: "column",
        },
        tableContainer: {
          overflowX: "auto",
          width: "100%",
        },
      };
    } else if (isTablet) {
      return {
        container: {
          display: "flex",
          gap: "20px",
          width: "95%",
          margin: "30px auto",
        },
        sidebar: {
          width: "35%",
          height: "max-content",
          background: "white",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: "20px",
        },
        content: {
          width: "75%",
        },
        scrollContent: {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "80vh",
          overflowY: "scroll",
          gap: "20px",
        },
        section: {
          padding: "20px",
          borderRadius: "10px",
        },
        sectionHeight: {
          height: "max-content",
        },
        imageGrid: {
          gap: "10px",
        },
        imagePreview: {
          width: "90px",
          height: "90px",
        },
        formFields: {
          flexDirection: "row",
        },
        tableContainer: {
          overflowX: "auto",
        },
      };
    } else {
      // Desktop styles (original)
      return {
        container: {
          display: "flex",
          gap: "30px",
          width: "95%",
          margin: "30px auto",
        },
        sidebar: {
          height: "max-content",
          width: "35%",
          background: "white",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: "23px",
        },
        content: {
          width: "80%",
        },
        scrollContent: {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "80vh",
          overflowY: "scroll",
          gap: "20px",
        },
        section: {
          padding: "23px",
          borderRadius: "10px",
        },
        sectionHeight: {
          height: "73.5vh",
        },
        imageGrid: {
          gap: "10px",
        },
        imagePreview: {
          width: "100px",
          height: "100px",
        },
        formFields: {
          flexDirection: "row",
        },
        tableContainer: {
          overflow: "hidden",
        },
      };
    }
  };

  const styles = getResponsiveStyles();

  // Mobile Navigation Component
  const MobileNavigation = () => (
    <>
      {isMobile && (
        <AppBar position="sticky" sx={{ backgroundColor: "#023DE1", mb: 2 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Add Product
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Quick Navigation
          </Typography>
          {quickMenu.map((data, index) => (
            <Box
              key={index}
              sx={{
                borderBottom: "1px solid #e6e6e6",
                py: 1.5,
                cursor: "pointer",
              }}
              onClick={() => handleScrollToSection(data.ref)}
            >
              <Typography
                variant="body1"
                sx={{
                  color: activeTab === data.ref ? "#023DE1" : "#888e91",
                  fontWeight: 600,
                }}
              >
                {data?.title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );

  return (
    <>
      <MobileNavigation />
      <form onSubmit={handleSubmit(AddNewProducts)}>
        <div style={styles.container}>
          {/* Desktop/Tablet Sidebar */}
          {!isMobile && (
            <div style={styles.sidebar}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <h3 style={{ fontWeight: "600", marginBottom: "5px" }}>
                  Quick Navigation
                </h3>
                {quickMenu.map((data, index) => (
                  <div
                    style={{
                      borderBottom: "1px solid #e6e6e6",
                      padding: "12px 0",
                    }}
                    key={index}
                    onClick={() => handleScrollToSection(data.ref)}
                  >
                    <h4
                      style={{
                        color: `${
                          activeTab === data.ref ? " #023DE1" : "#888e91"
                        }`,
                        fontWeight: `${activeTab === data.ref ? "600" : "600"}`,
                        cursor: "pointer",
                      }}
                    >
                      {data?.title}
                    </h4>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="scroll-content" style={styles.scrollContent}>
            {/* Product Information Section */}
            <div
              id="productInfoRef"
              ref={productInfoRef}
              style={{
                width: "100%",
                background: "white",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                ...styles.section,
                ...styles.sectionHeight,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "30px",
                }}
              >
                <h3 style={{ fontWeight: "600" }}>Product Information</h3>
                <Controller
                  name="productName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={!!errors.productName}
                      helperText={errors.productName?.message}
                      id="outlined-basic"
                      label="Product Name*"
                      variant="outlined"
                      fullWidth
                      size={isMobile ? "medium" : "small"}
                    />
                  )}
                />
                <Controller
                  name="categories"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {isNewCategory || categories.length === 0 ? (
                        <TextField
                          label="Category Name*"
                          variant="outlined"
                          fullWidth
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          error={!!errors.categories}
                          helperText={errors.categories?.message}
                          size={isMobile ? "medium" : "small"}
                        />
                      ) : (
                        <TextField
                          {...field}
                          select
                          SelectProps={{
                            multiple: true,
                          }}
                          label="Product Categories*"
                          variant="outlined"
                          fullWidth
                          error={!!errors.categories}
                          helperText={errors.categories?.message}
                          size={isMobile ? "medium" : "small"}
                        >
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.categoryName}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </div>
                  )}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "20px",
                    ...styles.formFields,
                  }}
                >
                  <Controller
                    name="productPrice"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        {...field}
                        onWheel={(e) => e.target.blur()} // Prevents scroll changes
                        error={!!errors.productPrice}
                        helperText={errors.productPrice?.message}
                        id="outlined-basic"
                        label="Price*"
                        variant="outlined"
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                  <Controller
                    name="productDiscountedPrice"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        onWheel={(e) => e.target.blur()} // Prevents scroll changes
                        {...field}
                        error={!!errors.productDiscountedPrice}
                        helperText={errors.productDiscountedPrice?.message}
                        id="outlined-basic"
                        label="Discounted Price"
                        variant="outlined"
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    alignItems: "flex-start",
                    ...styles.formFields,
                  }}
                >
                  <Controller
                    name="unitQuantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Unit Quantity*"
                        onWheel={(e) => e.target.blur()} // Prevents scroll changes
                        variant="outlined"
                        fullWidth
                        error={!!errors.unitQuantity}
                        helperText={errors.unitQuantity?.message}
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                  <TextField
                    label="Product Unit*"
                    value={watch("productUnit") || ""}
                    onClick={() => setIsUnitPopupOpen(true)}
                    fullWidth
                    error={!!errors.productUnit}
                    helperText={errors.productUnit?.message}
                    InputProps={{
                      readOnly: true,
                    }}
                    size={isMobile ? "medium" : "small"}
                  />
                </div>

                <ReactQuill
                  theme="snow"
                  value={value1}
                  onChange={setValue1}
                  style={{
                    width: "100%",
                    height: isMobile ? "100px" : "120px",
                    background: "white",
                    borderRadius: "8px",
                  }}
                />

                <div style={{ paddingBottom: "20px" }}></div>
              </div>
            </div>

            {/* Product Media Section */}
            <div
              id="productMediaRef"
              ref={productMediaRef}
              style={{
                width: "100%",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                ...styles.section,
                ...styles.sectionHeight,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "30px",
                }}
              >
                <h3 style={{ fontWeight: "600" }}>Product Media</h3>

                {/* Image Preview */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    ...styles.imageGrid,
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    id="image-upload"
                    onChange={handleImageUpload}
                    disabled={loading}
                  />
                  <label htmlFor="image-upload">
                    <div
                      style={{
                        border: "1px solid #e6e6e6",
                        cursor: "pointer",
                        borderRadius: "10px",
                        padding: isMobile ? "20px" : "30px",
                        ...styles.imagePreview,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CameraAltOutlinedIcon
                        sx={{
                          height: isMobile ? "30px" : "40px",
                          width: isMobile ? "30px" : "40px",
                        }}
                      />
                    </div>
                  </label>
                  {images.map((image, index) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        border: "1px solid #e6e6e6",
                        borderRadius: "10px",
                        overflow: "hidden",
                        ...styles.imagePreview,
                      }}
                    >
                      <AsyncImage
                        src={image}
                        alt={`Preview ${index}`}
                        style={{
                          ...styles.imagePreview,
                          objectFit: "contain",
                        }}
                        loader={
                          <div style={{ background: "rgb(230, 230, 230)" }} />
                        }
                        error={
                          <div style={{ background: "rgb(230, 230, 230)" }} />
                        }
                      />
                      <IconButton
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          color: "white",
                          backgroundColor: " #023DE1",
                          padding: isMobile ? "4px" : "8px",
                        }}
                        onClick={() => {
                          const updatedImages = images.filter(
                            (_, i) => i !== index
                          );
                          setImages(updatedImages);
                        }}
                        size={isMobile ? "small" : "medium"}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  {loading && (
                    <div
                      style={{
                        border: "1px solid #e6e6e6",
                        cursor: "pointer",
                        borderRadius: "10px",
                        padding: isMobile ? "20px" : "30px",
                        ...styles.imagePreview,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={24} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Inventory Section */}
            <div
              id="inventoryRef"
              ref={inventoryRef}
              style={{
                width: "100%",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                ...styles.section,
                ...(isMobile ? styles.sectionHeight : { height: "200px" }),
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "30px",
                }}
              >
                <h3 style={{ fontWeight: "600" }}>Inventory</h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "20px",
                    ...styles.formFields,
                  }}
                >
                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        onWheel={(e) => e.target.blur()} // Prevents scroll changes
                        {...field}
                        error={!!errors.quantity}
                        helperText={errors.quantity?.message}
                        id="outlined-basic"
                        label="Quantity*"
                        variant="outlined"
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                  <Controller
                    name="skuId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.skuId}
                        helperText={errors.skuId?.message}
                        id="outlined-basic"
                        label="SKU ID*"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Shipping and Tax Section */}
            <div
              id="shippingRef"
              ref={shippingRef}
              style={{
                width: "100%",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                ...styles.section,
                ...(isMobile ? styles.sectionHeight : { height: "200px" }),
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "30px",
                }}
              >
                <h3 style={{ fontWeight: "600" }}>Shipping and Tax</h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "20px",
                    ...styles.formFields,
                  }}
                >
                  <Controller
                    name="shippingCost"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        {...field}
                        onWheel={(e) => e.target.blur()} // Prevents scroll changes
                        error={!!errors.shippingCost}
                        helperText={errors.shippingCost?.message}
                        id="outlined-basic"
                        label="Shipping Cost*"
                        variant="outlined"
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                  <Controller
                    name="taxRate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        {...field}
                        error={!!errors.taxRate}
                        onWheel={(e) => e.target.blur()} // Prevents scroll changes
                        helperText={errors.taxRate?.message}
                        id="outlined-basic"
                        label="Tax Rate*"
                        variant="outlined"
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Coupons Section — link available coupons to this product */}
            <div
              id="couponsRef"
              ref={couponsRef}
              style={{
                width: "100%",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                ...styles.section,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <h3 style={{ fontWeight: "600", margin: 0 }}>Coupons</h3>
                <Typography variant="body2" color="text.secondary">
                  Select coupons to apply to this product. This updates each
                  coupon&apos;s applicable products list (same as Coupons tab).
                </Typography>

                {couponsLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading coupons…</Typography>
                  </Box>
                ) : (
                  <>
                    <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                      <InputLabel id="product-coupons-label">
                        Link coupons to this product
                      </InputLabel>
                      <Select
                        labelId="product-coupons-label"
                        multiple
                        value={selectedCouponIds}
                        onChange={(e) =>
                          setSelectedCouponIds(
                            typeof e.target.value === "string"
                              ? e.target.value.split(",")
                              : e.target.value
                          )
                        }
                        input={
                          <OutlinedInput label="Link coupons to this product" />
                        }
                        renderValue={(selected) =>
                          selected.length
                            ? selected
                                .map((id) => {
                                  const c = availableCoupons.find(
                                    (x) => x.id === id
                                  );
                                  return c
                                    ? (c.code || "").toUpperCase()
                                    : id;
                                })
                                .join(", ")
                            : "None selected"
                        }
                      >
                        {availableCoupons.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            <Checkbox
                              checked={selectedCouponIds.includes(c.id)}
                            />
                            <ListItemText
                              primary={`${(c.code || "").toUpperCase()} — ${couponDiscountLabel(c)}`}
                              secondary={
                                c.active === false
                                  ? "Inactive"
                                  : isGlobalCoupon(c)
                                    ? "Store-wide — linking will scope to selected products"
                                    : (c.applicableProducts || []).length
                                      ? `${(c.applicableProducts || []).length} product(s) linked`
                                      : (c.applicableCategories || []).length
                                        ? `${(c.applicableCategories || []).length} categor(y/ies)`
                                        : "Scoped coupon"
                              }
                            />
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Selected coupons get this product in their applicable
                        products list. Unselect to remove the link.
                      </FormHelperText>
                    </FormControl>

                    {!availableCoupons.length && (
                      <Typography variant="body2" color="text.secondary">
                        No coupons in the database yet. Create one in the
                        Coupons tab, then come back here to link it.
                      </Typography>
                    )}
                    {availableCoupons.length > 0 &&
                      availableCoupons.every((c) => c.active === false) && (
                        <Typography variant="body2" color="warning.main">
                          All coupons are currently inactive. You can still link
                          them here — turn them on in the Coupons tab when ready.
                        </Typography>
                      )}
                  </>
                )}
              </div>
            </div>

            {/* Variants Section */}
            <div
              id="variantsRef"
              ref={variantsRef}
              style={{
                width: "100%",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                ...styles.section,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <h3 style={{ fontWeight: "600" }}>Variants</h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "20px",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  {fields.map((data, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      {isEditing[index] ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "end",
                            flexDirection: "column",
                            gap: "10px",
                          }}
                        >
                          <h4
                            style={{
                              fontWeight: "600",
                              color: " #023DE1",
                              cursor: "pointer",
                            }}
                            onClick={() => handleDeleteVariant(index)}
                          >
                            Discard
                          </h4>

                          <Controller
                            name={`variants.${index}.optionName`}
                            control={control}
                            rules={{ required: "Option Name is required" }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Option Name"
                                variant="outlined"
                                fullWidth
                                error={!!errors.variants?.[index]?.optionName}
                                helperText={
                                  errors.variants?.[index]?.optionName?.message
                                }
                                size={isMobile ? "medium" : "small"}
                              />
                            )}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            width: "100%",
                            gap: "10px",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <h4>{watch().variants?.[index]?.optionName}</h4>
                          <IconButton
                            onClick={() => handleDeleteVariant(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      )}

                      {isEditing[index] ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            width: "100%",
                          }}
                        >
                          {watch().variants?.[index]?.optionValue?.map(
                            (value, optionIndex) => (
                              <div
                                key={optionIndex}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  flexDirection: isMobile ? "column" : "row",
                                }}
                              >
                                {watch().variants?.[
                                  index
                                ]?.optionName?.toLowerCase() === "color" ? (
                                  <Controller
                                    name={`variants.${index}.optionValue.${optionIndex}`}
                                    control={control}
                                    render={({
                                      field,
                                      fieldState: { error },
                                    }) => (
                                      <ColorPickerWithName
                                        value={field.value}
                                        onChange={(colorData) => {
                                          field.onChange(colorData);
                                        }}
                                        index={index}
                                        optionIndex={optionIndex}
                                        error={!!error}
                                      />
                                    )}
                                  />
                                ) : (
                                  <Controller
                                    name={`variants.${index}.optionValue.${optionIndex}`}
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        label={`Option Value ${
                                          optionIndex + 1
                                        }`}
                                        variant="outlined"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) =>
                                          field.onChange(e.target.value)
                                        }
                                        size={isMobile ? "medium" : "small"}
                                      />
                                    )}
                                  />
                                )}

                                {watch().variants?.[index]?.optionValue
                                  ?.length > 1 && (
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteOption(index, optionIndex)
                                    }
                                    size={isMobile ? "medium" : "small"}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </div>
                            )
                          )}

                          <h5
                            style={{
                              cursor: "pointer",
                              color: " #023DE1",
                              fontWeight: "600",
                              padding: "10px 0",
                            }}
                            onClick={() => handleAddNewOptionField(index)}
                          >
                            + Add new option
                          </h5>

                          <Button
                            style={{ width: "max-content" }}
                            variant="outlined"
                            onClick={() => handleDoneClick(index)}
                            size={isMobile ? "medium" : "small"}
                            disabled={
                              !watch().variants?.[index]?.optionName?.trim() ||
                              !watch().variants?.[index]?.optionValue?.some(
                                (value) => {
                                  console.log(value);
                                  const isColor =
                                    watch().variants?.[
                                      index
                                    ]?.optionName?.toLowerCase() === "color";

                                  if (isColor) {
                                    // Check for color object structure
                                    console.log(
                                      value?.name?.trim(),
                                      value?.hex?.trim(),
                                      "dwfsfsfd"
                                    );
                                    return (
                                      value?.name?.trim() && value?.hex?.trim()
                                    );
                                  }
                                  // Check for string value
                                  return typeof value === "string"
                                    ? value?.trim()
                                    : false;
                                }
                              )
                            }
                          >
                            Done
                          </Button>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "10px",
                          }}
                        >
                          {watch().variants?.[index]?.optionValue?.map(
                            (value, optionIndex) => (
                              <Chip
                                key={optionIndex}
                                label={
                                  watch().variants?.[
                                    index
                                  ]?.optionName?.toLowerCase() === "color" ? (
                                    <div
                                      style={{
                                        width: isMobile ? "20px" : "24px",
                                        height: isMobile ? "20px" : "24px",
                                        borderRadius: "50%",
                                        backgroundColor:
                                          typeof value === "object"
                                            ? value.hex
                                            : value,
                                        border: "1px solid black",
                                        cursor: "pointer",
                                      }}
                                    />
                                  ) : typeof value === "object" ? (
                                    value.name || value.hex
                                  ) : (
                                    value
                                  )
                                }
                                style={{
                                  borderRadius: "20px",
                                  background:
                                    watch().variants?.[
                                      index
                                    ]?.optionName?.toLowerCase() === "color"
                                      ? "transparent"
                                      : " #023DE1",
                                  color:
                                    watch().variants?.[
                                      index
                                    ]?.optionName?.toLowerCase() === "color"
                                      ? "inherit"
                                      : "white",
                                }}
                              />
                            )
                          )}
                          <IconButton onClick={() => handleEditClick(index)}>
                            <EditIcon />
                          </IconButton>
                        </div>
                      )}
                      <div
                        style={{
                          border: ".5px solid rgb(230, 230, 230)",
                          width: "100%",
                        }}
                      ></div>
                    </div>
                  ))}
                </div>
                <h5
                  style={{
                    cursor: "pointer",
                    color: " #023DE1",
                    fontWeight: "600",
                  }}
                  onClick={() => {
                    createVariant({ optionName: "", optionValue: [""] });
                    setIsEditing((prevState) => {
                      return {
                        ...prevState,
                        [watch().variants?.length - 1]: true,
                      };
                    });
                  }}
                >
                  + Add options like size and color
                </h5>
                {combinations.length > 0 && (
                  <div
                    style={{
                      border: ".5px solid rgb(230, 230, 230)",
                      width: "100%",
                      padding: isMobile ? "8px 0" : "16px 0",
                      borderRadius: "8px",
                      ...styles.tableContainer,
                    }}
                  >
                    <Table size={isMobile ? "small" : "medium"}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                          >
                            {variants.map(
                              (v, idx) =>
                                `${
                                  v?.optionName && idx !== variants.length - 1
                                    ? `${v?.optionName}/`
                                    : v?.optionName
                                }`
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                          >
                            SKU
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                          >
                            Inventory
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                          >
                            Price
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                          >
                            Discounted Price
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {combinations.map((comb, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                fontSize: isMobile ? "0.75rem" : "0.875rem",
                              }}
                            >
                              {comb.map(
                                (c, idx) =>
                                  `${c.value}${
                                    idx !== comb.length - 1 ? "/" : ""
                                  }`
                              )}
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`variantDetails.${index}.sku`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={
                                      !!errors.variantDetails?.[index]?.sku
                                    }
                                    helperText={
                                      errors.variantDetails?.[index]?.sku
                                        ?.message
                                    }
                                    sx={{
                                      minWidth: isMobile ? "80px" : "120px",
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`variantDetails.${index}.inventory`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    onWheel={(e) => e.target.blur()} // Prevents scroll changes
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={
                                      !!errors.variantDetails?.[index]
                                        ?.inventory
                                    }
                                    helperText={
                                      errors.variantDetails?.[index]?.inventory
                                        ?.message
                                    }
                                    sx={{
                                      minWidth: isMobile ? "60px" : "80px",
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`variantDetails.${index}.price`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    onWheel={(e) => e.target.blur()} // Prevents scroll changes
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={
                                      !!errors.variantDetails?.[index]?.price
                                    }
                                    helperText={
                                      errors.variantDetails?.[index]?.price
                                        ?.message
                                    }
                                    sx={{
                                      minWidth: isMobile ? "60px" : "80px",
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`variantDetails.${index}.discountedPrice`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    onWheel={(e) => e.target.blur()} // Prevents scroll changes
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={
                                      !!errors.variantDetails?.[index]
                                        ?.discountedPrice
                                    }
                                    helperText={
                                      errors.variantDetails?.[index]
                                        ?.discountedPrice?.message
                                    }
                                    sx={{
                                      minWidth: isMobile ? "60px" : "80px",
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Unit Selection Dialog */}
        <Dialog
          open={isUnitPopupOpen}
          onClose={() => setIsUnitPopupOpen(false)}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
        >
          <DialogTitle>
            Choose Product Unit
            <IconButton
              aria-label="close"
              onClick={() => setIsUnitPopupOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[600],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(auto-fit, minmax(80px, 1fr))"
                  : "repeat(auto-fit, minmax(100px, 1fr))",
                gap: isMobile ? "8px" : "10px",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              {productUnits.map((unit) => (
                <div
                  style={{
                    border: `1px solid ${
                      watch("productUnit") === unit
                        ? " #023DE1"
                        : "rgb(230, 230, 230)"
                    }`,
                    background:
                      watch("productUnit") === unit
                        ? "rgb(216, 230, 242)"
                        : "white",
                    padding: isMobile ? "6px 10px" : "8px 13px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    fontSize: isMobile ? "0.875rem" : "1rem",
                  }}
                  key={unit}
                  onClick={() => {
                    setValue("productUnit", unit);
                    setIsUnitPopupOpen(false);
                  }}
                >
                  {unit}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </>
  );
};

export default AddNewProduct;
